// useMe — thin wrapper around useAuthSession that exposes the server
// profile under a stable shape and a `refetch` action.
//
// `useAuthSession` is the lower-level Firebase + /api/auth/session sync;
// screens that just need role/handles/commentTrackingEnabled should use
// `useMe()` instead. The hook never returns the Firebase loading state —
// AppShell's outer auth gate already blocks render while that resolves,
// so by the time screens mount, `me` is either present or null.

import { useCallback } from 'react';
import type { SessionUser } from '@nayto/auth';
import { refreshMe, useAuthSession } from './session';

interface UseMeResult {
  me: SessionUser | null;
  /** True until the initial session sync completes. After that, refetches
   * are background — `me` stays populated while the new fetch is in flight. */
  isLoading: boolean;
  /** Re-run GET /api/me. Returns the fresh user (or null on failure). */
  refetch: () => Promise<SessionUser | null>;
}

export function useMe(): UseMeResult {
  const auth = useAuthSession();
  const refetch = useCallback(() => refreshMe(), []);
  return {
    me: auth.user,
    isLoading: auth.status === 'loading',
    refetch,
  };
}
